import React, { useCallback, useMemo, useRef, useState } from 'react';

import { DataState, Skeleton, deriveDataState } from '../loading';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useRelatedLists from '../../hooks/useRelatedLists';
import useRelatedListAccountState, { listStateKey } from '../../hooks/useRelatedListAccountState';
import { normalizeId } from '../../utils/ids';
import { RELATED_ITEMS_ENTRY_ID, RELATED_ITEMS_NAME } from '../../utils/relatedListSwitcher';
import RelatedItems from './RelatedItems';
import RelatedListSwitcher from './RelatedListSwitcher';
import RelatedListView from './RelatedListView';

const EMPTY_CARDS = Object.freeze({});
const NO_DEFINITIONS = Object.freeze([]);

/**
 * The item view's Related items section (KPR-719, epic KPE-029): the
 * existing Related items panel and, when the item's card defines related
 * lists, a switcher over them. It is mounted by the detail pane (and the
 * phone's inline read view, which is the same pane) and by the item page, and
 * takes exactly what `RelatedItems` takes.
 *
 * - A card with no list the caller can see: `RelatedItems` alone, as ever, and
 *   nothing is asked of the API. Which cards define lists comes from the card
 *   definition (`relatedLists`, served per card); which of them THIS caller is
 *   shown, and their counts, from `GET /api/items/{id}/related-lists`.
 * - With lists, a neutral switcher (not a second tab bar: platform-standards
 *   §13): Related items first with its content unchanged, then each list, every
 *   entry with its count under the list's default filter. The open list is
 *   `RelatedListView`. `RelatedItems` stays mounted, hidden, while a list is
 *   open: its relationships and its count keep their own life.
 * - The switcher's wait is a `DataState` of its own, so the panel under it is
 *   never held back, and a failed read says so with Retry.
 * - What the viewer chose in a list is kept here while the section lives, and
 *   their filter and grid/table choice are saved to their account
 *   (`useRelatedListAccountState`, KPR-722): a list they have not touched
 *   here opens on what the account holds; Reset forgets both.
 */
export default function RelatedItemsSection({
	itemId,
	collectionId,
	item,
	disabled = false,
	variant = 'panel',
	refreshKey = 0,
	onCountChange,
	origins,
}) {
	const resolvedItemId = useMemo(() => {
		const normalized = normalizeId(itemId);
		return normalized && normalized !== '-1' ? normalized : null;
	}, [itemId]);

	// ---- Which lists the card defines (no request) ----
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById || EMPTY_CARDS;
	const cardId = normalizeId(item?.card_id);
	const definitions = useMemo(() => {
		const served = cardId && Array.isArray(cardsById[cardId]?.relatedLists) ? cardsById[cardId].relatedLists : NO_DEFINITIONS;
		const shown = served.filter(entry => entry && entry.id && !entry.hidden);
		return shown.length ? shown : NO_DEFINITIONS;
	}, [cardsById, cardId]);
	const candidate = Boolean(resolvedItemId) && definitions.length > 0;

	// ---- Which of them the caller is shown, with counts ----
	const { lists, loading, hasData, error, retry } = useRelatedLists({ itemId: resolvedItemId, enabled: candidate, refreshKey });
	const shownLists = candidate && hasData ? lists : NO_DEFINITIONS;
	const hasLists = shownLists.length > 0;

	// ---- The first entry's count, from the panel itself ----
	const [firstCount, setFirstCount] = useState(0);
	const [firstLoaded, setFirstLoaded] = useState(false);
	const handleCount = useCallback((count) => {
		setFirstCount(count);
		if (onCountChange) onCountChange(count);
	}, [onCountChange]);

	// ---- Which entry is open; what the viewer chose in each list ----
	const [selectedId, setSelectedId] = useState(RELATED_ITEMS_ENTRY_ID);
	const [listStates, setListStates] = useState({});
	const listStatesRef = useRef(listStates);
	listStatesRef.current = listStates;
	// A list that is no longer there (another item's card, a list removed or
	// hidden since) falls back to the first entry.
	const openList = shownLists.find(entry => entry.id === selectedId) || null;
	const openId = openList ? openList.id : RELATED_ITEMS_ENTRY_ID;
	// What the viewer chose is kept per (defining card, list id) — the
	// account's key: a list id a clone copied onto another card is another
	// list, and this section outlives the item it was opened on.
	const openKey = listStateKey(openList);
	// The account's copy (KPR-722) seeds a list not yet touched here.
	const { seedFor, save: saveToAccount, reset: resetOnAccount } = useRelatedListAccountState({ itemId: resolvedItemId, lists: shownLists });
	// `persist: false` is a change the person did not make (the server's
	// canonical spelling of their filter): kept on screen, not saved.
	const handleViewState = useCallback((patch, { persist = true } = {}) => {
		if (!openList) return;
		const next = { ...(listStatesRef.current[openKey] || seedFor(openList)), ...patch };
		listStatesRef.current = { ...listStatesRef.current, [openKey]: next };
		setListStates(listStatesRef.current);
		if (persist && ('query' in patch || 'mode' in patch)) saveToAccount(openList, next);
	}, [openList, openKey, seedFor, saveToAccount]);
	// Reset: the list's default filter and the default view, here and on the account.
	const handleReset = useCallback(() => {
		if (!openList) return;
		listStatesRef.current = { ...listStatesRef.current, [openKey]: {} };
		setListStates(listStatesRef.current);
		resetOnAccount(openList);
	}, [openList, openKey, resetOnAccount]);

	const entries = useMemo(() => [
		{ id: RELATED_ITEMS_ENTRY_ID, name: RELATED_ITEMS_NAME, count: firstLoaded ? firstCount : null },
		...shownLists.map(entry => ({ id: entry.id, name: entry.name, count: entry.count })),
	], [shownLists, firstCount, firstLoaded]);

	const embedded = variant === 'embedded';
	// The page's panel draws its frame and heading here, once, above the
	// switcher; the panel inside is then the embedded one. While the lists are
	// being asked for the frame is already here, so the answer does not move it.
	const framed = !embedded && candidate && !(hasData && !hasLists);

	return (
		<div className={framed ? 'related-items related-items--section' : 'related-section'}>
			{framed ? <div className='micro-label related-items__title related-section__title'>{RELATED_ITEMS_NAME}</div> : null}

			{candidate && (hasLists || !hasData) ? (
				<DataState
					className={`related-section__switcher${hasData ? '' : ' related-section__switcher--pending'}`}
					state={deriveDataState({ loading: loading || (!hasData && !error), error, hasData, isEmpty: false })}
					hasData={hasData}
					skeleton={<Skeleton variant='block' height={32} width={240} />}
					error={error}
					onRetry={retry}
					label='related lists'
				>
					<RelatedListSwitcher entries={entries} value={openId} onChange={setSelectedId} />
				</DataState>
			) : null}

			<div className='related-section__first' hidden={Boolean(openList)}>
				<RelatedItems
					itemId={itemId}
					collectionId={collectionId}
					item={item}
					disabled={disabled}
					variant={framed ? 'embedded' : variant}
					refreshKey={refreshKey}
					onCountChange={handleCount}
					onLoadedChange={setFirstLoaded}
					{...(origins ? { origins } : {})}
				/>
			</div>

			{openList ? (
				<div className='related-section__list' role='region' aria-label={openList.name}>
					<RelatedListView
						key={openKey}
						itemId={resolvedItemId}
						collectionId={collectionId}
						list={openList}
						definition={definitions.find(entry => entry.id === openList.id) || null}
						viewState={listStates[openKey] || seedFor(openList)}
						onViewState={handleViewState}
						onReset={handleReset}
						refreshKey={refreshKey}
						onListsChanged={retry}
						onRetryLists={retry}
					/>
				</div>
			) : null}
		</div>
	);
}
